import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import "../landing.css";
import "../blog.css"; // pt. .lp-blog-card din secțiunea "Din blog" (folosită direct aici, nu doar în /blog)
import LogoMark from "../components/LogoMark";
import { API_BASE } from "../api";
import PopupCTA from "../components/PopupCTA";
import NTPLogo from "ntp-logo-react";

export default function Landing() {
  const nav = useNavigate();
  const [navOpen, setNavOpen]     = useState(false);
  const [scoreAnim, setScoreAnim] = useState(0);
  const [latestPosts, setLatestPosts] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) nav("/dashboard");

    document.title = "FiscalRisk — Evaluare risc fiscal online | SAF-T | Raport PDF";

    const prev = document.body.style.background;
    document.body.style.background = "#0b0f19";

    // JSON-LD date structurate pentru Google
    const ldJson = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "name": "FiscalRisk",
          "url": "https://www.risc-fiscal.ro",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://www.risc-fiscal.ro/blog?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@type": "Organization",
          "name": "FiscalRisk",
          "url": "https://www.risc-fiscal.ro",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.risc-fiscal.ro/logo512.png"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "risc.fiscal@gmail.com",
            "contactType": "customer support",
            "availableLanguage": "Romanian"
          },
          "description": "Platformă online de evaluare a riscului fiscal pentru firme din România. Scor 0-100, analiză SAF-T automată, raport PDF.",
          "areaServed": "RO",
          "inLanguage": "ro"
        },
        {
          "@type": "SoftwareApplication",
          "name": "FiscalRisk",
          "url": "https://www.risc-fiscal.ro",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "147",
            "priceCurrency": "RON",
            "description": "Raport de evaluare risc fiscal — mini audit intern"
          },
          "description": "Platformă online de evaluare a riscului fiscal pentru firme din România. Scor de risc 0–100, analiză SAF-T automată, raport PDF tip mini audit intern.",
          "inLanguage": "ro"
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Ce este riscul fiscal al unei firme?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Riscul fiscal reprezintă probabilitatea ca o firmă să fie supusă unor controale fiscale, penalități, dobânzi sau corecții din partea autorităților (ANAF). El crește atunci când există neconcordanțe între declarații și evidențele contabile, plăți restante, perioade de inactivitate sau lipsă de documente justificative. Identificarea timpurie permite luarea de măsuri preventive înainte ca ele să devină probleme reale."
              }
            },
            {
              "@type": "Question",
              "name": "Ce este SAF-T și cum ajută la evaluarea riscului fiscal?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "SAF-T (Standard Audit File for Tax) este un fișier XML standardizat, cerut obligatoriu de ANAF, care conține datele contabile complete ale firmei. FiscalRisk analizează automat fișierul SAF-T pentru a extrage indicatori financiari: marja de profit, fluxul de numerar, concentrarea veniturilor, ponderea notelor de credit și corelarea TVA-ului colectat cu cel deductibil, permițând o evaluare mult mai precisă a riscului."
              }
            },
            {
              "@type": "Question",
              "name": "Ce este verificarea D300 și e obligatorie?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "D300 este decontul de TVA depus la ANAF. Poți atașa opțional PDF-ul original D300 lângă un SAF-T deja încărcat, iar FiscalRisk compară automat TVA colectată, deductibilă și soldul de TVA dintre cele două declarații. Nu este obligatoriu — raportul funcționează și doar pe baza SAF-T."
              }
            },
            {
              "@type": "Question",
              "name": "Cine are acces la fișierele SAF-T și D300 pe care le încarc?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Fișierele originale nu sunt stocate pe server — se folosesc doar cât durează analiza, apoi se șterg definitiv. Rămâne doar rezultatul analizei, vizibil exclusiv în contul tău. Datele nu sunt niciodată partajate cu alți utilizatori, cu concurența sau cu terți."
              }
            },
            {
              "@type": "Question",
              "name": "FiscalRisk înlocuiește un audit fiscal complet?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Nu. FiscalRisk oferă un mini audit intern cu caracter informativ și orientativ. Evaluarea nu are valoare juridică, nu reprezintă o decizie a autorităților fiscale și nu înlocuiește consultanța unui expert contabil autorizat, auditor financiar sau consultant fiscal. Scopul este să ofere o imagine de ansamblu rapidă, utilă pentru decizii interne."
              }
            },
            {
              "@type": "Question",
              "name": "Cine ar trebui să folosească FiscalRisk?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "FiscalRisk este util pentru antreprenori care vor să știe unde se situează firma lor fiscal, pentru directori financiari care monitorizează mai multe entități, pentru contabili care vor să ofere clienților un instrument de verificare internă și pentru firmele care se pregătesc pentru o inspecție fiscală sau audit extern."
              }
            },
            {
              "@type": "Question",
              "name": "Când ar trebui să generez un raport de risc fiscal?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Momentele cu cel mai mare impact sunt legate de termenele de raportare: înainte de depunerea declarației de TVA (25 ale lunii sau trimestrial), înainte de depunerea SAF-T, înainte de închiderea exercițiului financiar și înainte de depunerea bilanțului. Rapoartele periodice permit urmărirea evoluției scorului în timp."
              }
            },
            {
              "@type": "Question",
              "name": "Cum interpretez scorul de risc obținut?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Scorul este pe o scală de la 0 la 100: 0–25 risc scăzut, 26–50 risc moderat, 51–75 risc ridicat, 76–100 risc critic. Fiecare scor este însoțit de observații specifice care explică ce a contribuit la acel punctaj și ce acțiuni ar putea reduce riscul în perioadele următoare."
              }
            }
          ]
        }
      ]
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-json-fiscalrisk";
    script.text = JSON.stringify(ldJson);
    document.head.appendChild(script);

    // Animație scor 0 → 63
    let v = 0;
    const tick = setInterval(() => {
      v = Math.min(v + 1, 63);
      setScoreAnim(v);
      if (v >= 63) clearInterval(tick);
    }, 20);

    const onScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", onScroll);

    return () => {
      document.body.style.background = prev;
      clearInterval(tick);
      window.removeEventListener("scroll", onScroll);
      const el = document.getElementById("ld-json-fiscalrisk");
      if (el) el.remove();
    };
  }, [nav]);

  useEffect(() => {
    fetch(`${API_BASE}/blog/posts/latest?n=3`)
      .then(r => r.json())
      .then(setLatestPosts)
      .catch(() => {});
  }, []);

  return (
    <div className="lp">
      <header className="lp-header glass">
        <a href="#" className="lp-brand" onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <LogoMark size={38} />
          <div>
            <div className="lp-name">FiscalRisk</div>
            <div className="lp-tag">Evaluare internă • Conformitate fiscală</div>
          </div>
        </a>

        <button className="hamburger" onClick={() => setNavOpen((o) => !o)} aria-label="Meniu">
          <span /><span /><span />
        </button>

        <nav className={`lp-nav${navOpen ? " open" : ""}`}>
          <a className="lp-navlink" href="#cum-functioneaza" onClick={() => setNavOpen(false)}>
            Cum funcționează
          </a>
          <a className="lp-navlink" href="#preturi" onClick={() => setNavOpen(false)}>
            Prețuri
          </a>
          <Link className="lp-navlink" to="/blog" onClick={() => setNavOpen(false)}>
            Blog
          </Link>
          <Link className="lp-navlink" to="/calculatoare" onClick={() => setNavOpen(false)}>
            Calculatoare
          </Link>
          <Link className="lp-navlink" to="/metodologie" onClick={() => setNavOpen(false)}>
            Metodologie
          </Link>
          <Link className="btn ghost" to="/login" onClick={() => setNavOpen(false)}>Login</Link>
          <Link className="btn primary" to="/signup" onClick={() => setNavOpen(false)}>Creează cont</Link>
        </nav>
      </header>

      <main className="lp-main">

        {/* ── HERO ────────────────────────────────────────────── */}
        <section className="lp-hero">
          <div className="lp-hero-left">
            <div className="pill">
              Evaluare risc fiscal online • Analiză SAF-T • Raport PDF instant
            </div>

            <h1 className="lp-h1">
              Cât de expusă este firma ta la un <span className="hand-underline">control ANAF</span>?
            </h1>

            <p className="lp-sub">
              FiscalRisk analizează vectorul fiscal, datele publice ANAF și declarațiile SAF-T
              ale firmei tale și generează un <strong style={{color:"#e9ecf2"}}>scor de risc fiscal 0–100</strong> cu
              observații tip mini-audit intern. Detectează automat obligații fiscale neachitate,
              verifică prin sondaj statusul fiscal al clienților și furnizorilor importanți și
              identifică întârzierile la buget direct din SAF-T. Primești raportul PDF în câteva
              minute, înainte să apară problemele — nu după.
            </p>

            <div className="lp-cta">
              <Link className="btn big" to="/signup">Evaluează riscul firmei tale →</Link>
              <a className="btn ghost" href="#cum-functioneaza">Cum funcționează ↓</a>
            </div>

            <div className="lp-trust">
              <div className="trust-item">✔ Cont gratuit, fără card</div>
              <div className="trust-item">✔ Raport PDF instant</div>
              <div className="trust-item">✔ Date securizate · GDPR</div>
            </div>
          </div>

          {/* CARD DEMO animat */}
          <div className="lp-hero-right">
            <div className="demo-card glass animate-float" style={{ position: "relative", overflow: "hidden" }}>
              {/* Linie de scanare — animație CSS */}
              <div className="demo-scan" />

              <div className="demo-top">
                <div className="dots">
                  <span className="dot red" />
                  <span className="dot yellow" />
                  <span className="dot green" />
                </div>
                <div className="demo-title">Raport intern – Evaluare risc fiscal</div>
              </div>

              <div className="demo-body">
                <div className="demo-row">
                  <div>
                    <div className="demo-label">Scor</div>
                    <div className="demo-score">{scoreAnim} / 100</div>
                  </div>
                  <div className="badge orange">RIDICAT</div>
                </div>

                <div className="demo-section">
                  <div className="demo-h">Observații (mini-audit)</div>
                  <ul className="demo-list">
                    <li>
                      Întârzierile la plată pot genera accesorii și cresc probabilitatea acumulării
                      de restanțe, ceea ce poate atrage atenția la o analiză ulterioară.
                    </li>
                    <li>
                      Antecedentele de TVA suspendat impun verificarea vectorului fiscal și a statusului
                      curent TVA, pentru evitarea neconformităților recurente.
                    </li>
                    <li>
                      Lipsa SAF-T reduce capacitatea de validare a corelațiilor dintre declarații
                      și evidențele contabile, crescând incertitudinea evaluării.
                    </li>
                  </ul>
                </div>

                <div className="demo-foot">
                  Recomandare: reverificați scorul de risc fiscal lunar sau trimestrial, pentru o prevenție eficientă.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── AVANTAJE ────────────────────────────────────────── */}
        <section className="lp-section">
          <h2 className="lp-h2">De ce să folosești FiscalRisk</h2>
          <p className="lp-muted" style={{marginBottom: 16}}>
            Pe scurt: vrem să știi exact unde stai fiscal, nu să afli surprins la un control.
          </p>
          <div className="grid3">
            <div className="card glass">
              <h3>Știi cum arată firma ta din perspectiva ANAF?</h3>
              <p>
                Majoritatea antreprenorilor nu știu — și află abia la control.
                FiscalRisk îți arată un scor de risc de la 0 la 100, cu observații formulate
                în română, fără jargon contabil. Înțelegi dintr-o privire dacă totul e în regulă
                sau dacă există ceva care merită atenție — înainte să te sune cineva de la ANAF.
              </p>
            </div>
            <div className="card glass">
              <h3>Ești contabil? Tot portofoliul, dintr-o privire</h3>
              <p>
                Nu mai verifica manual fiecare client. Adaugi firmele în platformă și vezi
                imediat care are risc ridicat, unde trebuie să intervii prioritar și ce
                documente lipsesc. FiscalRisk îți oferă date concrete despre expunerea fiscală
                a fiecărui client — inclusiv pentru cei noi, înainte de a semna ceva.
              </p>
            </div>
            <div className="card glass">
              <h3>SAF-T se potrivește cu decontul de TVA depus?</h3>
              <p>
                Poți atașa opțional decontul de TVA (<b>D300</b>) lângă SAF-T-ul firmei tale —
                FiscalRisk compară automat TVA colectată, deductibilă și soldul de TVA din cele
                două declarații. Afli imediat dacă se corelează, aceeași verificare pe care ANAF
                a testat-o recent într-un program-pilot pe scară largă.
              </p>
            </div>
          </div>
        </section>

        {/* ── PENTRU CINE — mutat înainte de Cum funcționează ── */}
        <section className="lp-section" id="pentru-cine">
          <h2 className="lp-h2" style={{ textAlign: "center" }}>Pentru contabili și antreprenori</h2>
          <p className="lp-muted lp-muted--2col" style={{ marginBottom: 24 }}>
            Lumea fiscală din România devine tot mai complicată: e-Factura, SAF-T, e-TVA, e-Transport,
            modificări la microîntreprinderi. Nu îți putem prelua responsabilitățile fiscale, dar îți
            putem arăta rapid unde stai — ce funcționează bine și ce merită verificat, înainte ca
            problemele să devină urgențe.
          </p>

          <div className="persona-grid">
            <div className="persona-card glass persona-card--blue">
              <div className="persona-header">
                <span className="persona-badge persona-badge--blue">C</span>
                <h3 className="persona-title">Ești contabil?</h3>
              </div>
              <p className="persona-text">
                Gestionezi un portofoliu de firme cu termene strânse: depuneri SAF-T,
                reconcilieri e-TVA, vector fiscal, declarații D394, bilanțuri.
                Cu FiscalRisk poți vedea dintr-o privire{" "}
                <strong>care dintre clienții tăi are un nivel de risc ridicat</strong>{" "}
                și unde trebuie să intervii prioritar — înainte ca ANAF să o facă.
                Un instrument simplu care completează expertiza ta cu date concrete
                despre expunerea fiscală a fiecărui client.
              </p>
              <div className="persona-divider" />
              <div className="persona-footer">
                <div className="persona-cta">Monitorizează riscul întregului portofoliu →</div>
                <Link className="btn persona-btn persona-btn--blue" to="/signup">Creează cont gratuit</Link>
              </div>
            </div>

            <div className="persona-card glass persona-card--gold">
              <div className="persona-header">
                <span className="persona-badge persona-badge--gold">A</span>
                <h3 className="persona-title">Ești antreprenor?</h3>
              </div>
              <p className="persona-text">
                Ai contabilul tău și te bazezi pe el — dar poate nu știi exact cum arată firma
                ta din perspectiva ANAF. Există obligații care contează: TVA la zi, declarații
                depuse la termen, SAF-T, e-Factura. FiscalRisk îți arată{" "}
                <strong>un scor de risc calculat automat</strong>{" "}
                pe baza datelor reale ale firmei tale, astfel încât să știi din timp dacă
                e totul în regulă sau dacă merită o discuție urgentă cu contabilul tău.
              </p>
              <div className="persona-divider" />
              <div className="persona-footer">
                <div className="persona-cta">Verifică starea fiscală a firmei tale →</div>
                <Link className="btn persona-btn persona-btn--gold" to="/signup">Verifică firma ta</Link>
              </div>
            </div>
          </div>

          <div className="disclaimer glass" style={{ marginTop: 28 }}>
            <b>Notă importantă:</b> FiscalRisk nu este un instrument oficial al ANAF și nu evaluează riscul
            conform vreunei metodologii recunoscute legal. Scorul de risc reprezintă <b>exclusiv o interpretare
            financiară consultativă</b>, generată algoritmic pe baza datelor introduse de utilizator și a
            informațiilor publice disponibile. Evaluarea nu are valoare juridică, nu constituie un aviz fiscal
            și nu înlocuiește consultanța unui expert contabil autorizat, auditor financiar sau consultant fiscal.
            Scorul obținut nu garantează absența unui control fiscal și nu poate fi invocat în relația cu
            autoritățile fiscale.
          </div>
        </section>

        {/* ── CUM FUNCȚIONEAZĂ ────────────────────────────────── */}
        <section className="lp-section" id="cum-functioneaza">
          <h2 className="lp-h2">Cum funcționează</h2>
          <p className="lp-muted">
            Fluxul este gândit să fie simplu: adaugi firmă → completezi câteva informații → încarci documente → obții raport.
          </p>

          <div className="steps">
            <div className="step glass">
              <div className="step-n">1</div>
              <div>
                <div className="step-t">Creezi cont cu email și parolă</div>
                <div className="step-d">
                  La înregistrare primești o parolă inițială pe email. Datele tale și rapoartele sunt
                  accesibile doar din contul tău.
                </div>
              </div>
            </div>

            <div className="step glass">
              <div className="step-n">2</div>
              <div>
                <div className="step-t">Adaugi firmele (CUI) în contul tău</div>
                <div className="step-d">
                  Fiecare firmă este asociată contului tău, astfel încât doar tu poți vedea scorurile și rapoartele ei.
                </div>
              </div>
            </div>

            <div className="step glass">
              <div className="step-n">3</div>
              <div>
                <div className="step-t">Încarci SAF-T și completezi date relevante</div>
                <div className="step-d">
                  Ai nevoie de fișierul SAF-T (Declarația D406) exportat din programul contabil —
                  Saga, WinMentor, Ciel etc. — sau solicitat contabilului tău — <b>pentru o perioadă cu activitate</b> (facturi, plăți înregistrate).
                  Completările din vectorul fiscal adaugă context pentru o evaluare mai precisă.
                  Poți atașa opțional și decontul de TVA (D300) pentru o verificare automată și mai precisă.
                </div>
              </div>
            </div>

            <div className="step glass">
              <div className="step-n">4</div>
              <div>
                <div className="step-t">Generezi raport PDF și urmărești istoricul</div>
                <div className="step-d">
                  Primești raportul PDF și îl păstrezi în cont; pe măsură ce completezi mai multe perioade,
                  acuratețea crește și poți vedea evoluția riscului.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ — mutat înainte de Prețuri ──────────────────── */}
        <section className="lp-section" id="faq">
          <h2 className="lp-h2">Întrebări frecvente despre riscul fiscal</h2>
          <p className="lp-muted">Tot ce trebuie să știi despre evaluarea riscului fiscal, analiza SAF-T și cum funcționează FiscalRisk.</p>
          <div className="faq">
            {[
              {
                q: "Ce este riscul fiscal al unei firme?",
                a: "Riscul fiscal reprezintă probabilitatea ca o firmă să fie supusă unor controale fiscale, penalități, dobânzi sau corecții din partea autorităților (ANAF). Spre deosebire de riscul contabil sau juridic, riscul fiscal vizează în mod direct relația firmei cu obligațiile față de stat: TVA, impozit pe profit, contribuții sociale și respectarea termenelor de depunere a declarațiilor. El crește atunci când există neconcordanțe între declarații și evidențele contabile, plăți restante, perioade de inactivitate sau lipsă de documente justificative. Identificarea timpurie a acestor factori permite luarea de măsuri preventive înainte ca ele să devină probleme reale."
              },
              {
                q: "Ce este SAF-T și cum ajută la evaluarea riscului fiscal?",
                a: "SAF-T (Standard Audit File for Tax) este un fișier XML standardizat, cerut obligatoriu de ANAF, care conține datele contabile complete ale firmei: planul de conturi, solduri, tranzacții, facturi emise și primite. FiscalRisk analizează automat fișierul SAF-T pentru a extrage indicatori financiari relevanți: marja de profit, fluxul de numerar disponibil, concentrarea veniturilor pe un număr redus de clienți, ponderea notelor de credit și corelarea TVA-ului colectat cu cel deductibil. Acești indicatori permit o evaluare mult mai precisă a riscului față de situația în care firma nu are SAF-T încărcat."
              },
              {
                q: "Ce tip de fișier SAF-T trebuie să încarc pentru o evaluare corectă?",
                a: "Încarcă fișierul SAF-T (D406) exportat din programul de contabilitate pentru o perioadă în care firma a avut activitate — facturi emise, facturi primite, plăți înregistrate. Dacă fișierul nu conține tranzacții (perioadă fără activitate sau export greșit), aplicația va detecta automat și va afișa un avertisment. Fișierul corect are de obicei câteva zeci sau sute de KB și poate fi exportat din orice program de contabilitate agreat ANAF: Saga, WinMentor, Ciel, SmartBill, OMNIDATA etc. Dacă nu ai acces direct la program, solicită contabilului tău fișierul SAF-T D406 lunar sau trimestrial pentru perioada dorită."
              },
              {
                q: "Ce este verificarea D300 și e obligatorie?",
                a: "D300 este decontul de TVA depus la ANAF. Dacă vrei o evaluare și mai precisă, poți atașa opțional PDF-ul original D300 lângă un SAF-T deja încărcat — FiscalRisk compară automat TVA colectată, deductibilă și soldul de TVA dintre cele două declarații și îți arată dacă se corelează. Nu este obligatoriu — dacă nu îl atașezi, raportul funcționează normal doar pe baza SAF-T, cu o notă care te invită să verifici manual."
              },
              {
                q: "Cine are acces la fișierele SAF-T și D300 pe care le încarc?",
                a: "Nimeni în afară de tine. Fișierele originale nu sunt stocate pe server — se folosesc doar cât durează analiza automată, apoi se șterg definitiv. Rămâne doar rezultatul analizei (cifrele extrase), vizibil exclusiv în contul tău. Din același motiv, fișierele nu mai pot fi descărcate ulterior din cont — ca nici măcar cineva care ar accesa neautorizat contul tău să nu poată scoate declarațiile firmei. Datele tale nu sunt niciodată partajate cu alți utilizatori, cu concurența sau cu terți."
              },
              {
                q: "FiscalRisk înlocuiește un audit fiscal complet?",
                a: "Nu, și este important să fie clar: FiscalRisk oferă un mini audit intern cu caracter informativ și orientativ. Evaluarea se bazează exclusiv pe datele introduse de utilizator și pe informațiile publice disponibile (ANAF). Ea nu are valoare juridică, nu reprezintă o decizie sau un aviz al autorităților fiscale și nu înlocuiește consultanța unui expert contabil autorizat, a unui auditor financiar sau a unui consultant fiscal. Scopul este să ofere o imagine de ansamblu rapidă, utilă pentru decizii interne, nu pentru relația cu autoritățile."
              },
              {
                q: "Cine ar trebui să folosească FiscalRisk?",
                a: "FiscalRisk este util pentru mai multe categorii: antreprenorii care vor să știe unde se situează firma lor din punct de vedere fiscal fără să aștepte un control; directorii financiari care monitorizează mai multe entități și au nevoie de o imagine periodică a conformității; contabilii care vor să ofere clienților un instrument suplimentar de verificare internă; și firmele care se pregătesc pentru o inspecție fiscală sau pentru un audit extern și vor să identifice din timp eventualele vulnerabilități."
              },
              {
                q: "Când ar trebui să generez un raport de risc fiscal?",
                a: "Momentele cu cel mai mare impact sunt cele legate de termenele de raportare: înainte de depunerea declarației de TVA (25 ale lunii sau trimestrial), înainte de depunerea SAF-T, înainte de închiderea exercițiului financiar și înainte de depunerea bilanțului. De asemenea, este util să generezi un raport după orice schimbare semnificativă în structura firmei, după modificări ale regimului TVA sau după o perioadă de activitate redusă. Rapoartele periodice permit urmărirea evoluției scorului în timp și validarea îmbunătățirilor de conformitate."
              },
              {
                q: "Cum interpretez scorul de risc obținut?",
                a: "Scorul este calculat pe o scală de la 0 la 100 și este împărțit în patru niveluri: 0–25 risc scăzut (firma nu prezintă factori de risc semnificativi identificați), 26–50 risc moderat (există câțiva factori care merită atenție), 51–75 risc ridicat (mai mulți factori de risc activi care necesită acțiuni concrete), 76–100 risc critic (combinație de factori cu potențial ridicat de atragere a unui control fiscal). Fiecare scor este însoțit de observații specifice care explică exact ce a contribuit la acel punctaj și ce acțiuni ar putea reduce riscul în perioadele următoare."
              },
            ].map(({ q, a }, i) => (
              <details key={i} className="faq-item glass">
                <summary className="faq-q">{q}</summary>
                <p className="faq-a">{a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ── PREȚURI — mutat după FAQ ─────────────────────────── */}
        <section className="lp-section" id="preturi">
          <h2 className="lp-h2">Prețuri</h2>
          <p className="lp-muted">
            Contul este gratuit. Plătești doar când generezi un raport PDF — per firmă, per raport.
          </p>

          <div className="pricing">
            {/* Stânga — Ce include */}
            <div className="price-card glass">
              <div className="price-top">
                <div className="price-name">Ce include raportul</div>
              </div>
              <ul className="price-list" style={{ marginTop: 14 }}>
                <li>Scor de risc 0–100 pe niveluri clare</li>
                <li>Observații formulate ca într-un audit intern</li>
                <li>Analiză automată a fișierului SAF-T</li>
                <li>Indicatori financiari: marjă profit, flux numerar, concentrare clienți</li>
                <li>Factori de risc administrativ (TVA, declarații, vechime firmă)</li>
                <li>Detectare automată a obligațiilor fiscale neachitate (TVA, impozit, contribuții)</li>
                <li>Verificare prin sondaj a statusului fiscal al clienților și furnizorilor importanți la ANAF</li>
              </ul>
            </div>

            {/* Centru — Preț */}
            <div className="price-card glass featured">
              <div className="price-top">
                <div className="price-name">Raport de evaluare risc fiscal</div>
              </div>
              <div className="price">
                <span className="now">147 lei</span>
              </div>
              <div style={{ fontSize: 12, color: "rgba(233,236,242,0.5)", marginBottom: 14 }}>
                preț per raport, TVA inclus
              </div>
              <ul className="price-list">
                <li>Contul este gratuit</li>
                <li>Plătești doar la generarea raportului PDF</li>
                <li>Raportul rămâne în cont 12 luni</li>
              </ul>
              <Link className="btn big full" to="/signup">Creează cont gratuit</Link>
            </div>

            {/* Dreapta — Cum funcționează plata */}
            <div className="price-card glass">
              <div className="price-top">
                <div className="price-name">Cum funcționează</div>
              </div>
              <ul className="price-list" style={{ marginTop: 14 }}>
                <li>Creezi cont gratuit — fără card necesar</li>
                <li>Adaugi firmele și încarci datele oricând</li>
                <li>Când vrei raportul, apeși „Generează" și plătești online cu cardul</li>
                <li>PDF-ul se generează instant și rămâne salvat în cont</li>
                <li>Poți genera rapoarte noi oricând, pentru aceeași firmă sau alta</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── BLOG PREVIEW — după Prețuri ──────────────────────── */}
        {latestPosts.length > 0 && (
          <section className="lp-section" id="blog-preview">
            <h2 className="lp-h2">Din blog</h2>
            <p className="lp-muted" style={{ marginBottom: 24 }}>
              Ghiduri practice despre risc fiscal, SAF-T, TVA și conformitate fiscală pentru firme din România.
            </p>
            <div className="lp-blog-grid">
              {latestPosts.map(p => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="lp-blog-card glass">
                  {p.cover_image_url
                    ? <img src={p.cover_image_url} alt={p.title} className="lp-blog-card__img" />
                    : <div className="lp-blog-card__img-placeholder">📊</div>
                  }
                  <div className="lp-blog-card__body">
                    {p.category && <span className="lp-blog-card__cat">{p.category.name}</span>}
                    <h3 className="lp-blog-card__title">{p.title}</h3>
                    {p.excerpt && <p className="lp-blog-card__excerpt">{p.excerpt}</p>}
                    <span className="lp-blog-card__read">Citește articolul →</span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="lp-blog-more">
              <Link to="/blog">→ Vezi toate articolele din blog</Link>
            </div>
          </section>
        )}

      </main>

      {/* ── FOOTER — Call to action ──────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-cta">
          <div className="lp-footer-cta-text">
            <div className="lp-footer-cta-title">Gata să afli cum stă firma ta?</div>
            <p className="lp-footer-cta-sub">Cont gratuit, fără card. Plătești doar când generezi raportul PDF.</p>
          </div>
          <Link className="btn big" to="/signup">Verifică acum →</Link>
        </div>
        <div className="lp-footer-bottom">
          <div className="lp-footer-links">
            <a href="#preturi">Prețuri</a>
            <a href="#cum-functioneaza">Cum funcționează</a>
            <Link to="/calculatoare">Calculatoare fiscale</Link>
            <Link to="/metodologie">Metodologie scor</Link>
            <Link to="/termeni">Termeni și condiții</Link>
            <Link to="/confidentialitate">Confidențialitate & GDPR</Link>
            <Link to="/cookies">Cookies</Link>
            <a href="mailto:risc.fiscal@gmail.com">Contact</a>
          </div>
          <div className="lp-footer-social">
            <a href="https://www.facebook.com/people/FiscalRisk-Rom%C3%A2nia/61593053570885/" target="_blank" rel="noopener noreferrer">Facebook</a>
            <a href="https://www.instagram.com/risc.fiscal/" target="_blank" rel="noopener noreferrer">Instagram</a>
            <a href="https://www.linkedin.com/company/fiscal-risk" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          </div>
          <div className="lp-footer-trust">
            <a href="https://netopia-payments.com" target="_blank" rel="noopener noreferrer" aria-label="Plată securizată prin NETOPIA Payments">
              <NTPLogo color="#ffffff" version="horizontal" secret="166105" />
            </a>
            <span className="lp-footer-sep">|</span>
            <a href="https://anpc.ro/sal/" target="_blank" rel="noopener noreferrer" aria-label="SAL — Soluționarea Alternativă a Litigiilor (ANPC)">
              <img src="/anpc-sal-logo.png" alt="ANPC SAL — Soluționarea Alternativă a Litigiilor" className="anpc-badge" />
            </a>
          </div>
          <div className="lp-footer-contact">
            <strong>SOUTH LEX ELECTRONICS S.R.L.</strong> &nbsp;·&nbsp; CUI: RO46321971 &nbsp;·&nbsp; J34/503/2022 &nbsp;·&nbsp;
            Str. Dunării, Bl. Patria, Sc. A, Et. 7, Ap. 30, Mun. Alexandria, Jud. Teleorman &nbsp;·&nbsp;
            <a href="tel:+40721512077">0721 512 077</a> &nbsp;·&nbsp;
            <a href="mailto:risc.fiscal@gmail.com">risc.fiscal@gmail.com</a>
          </div>
          <div>© 2026 FiscalRisk — Toate drepturile rezervate</div>
        </div>
      </footer>

      {/* ── SCROLL TO TOP — doar mobil ───────────────────────── */}
      {showScrollTop && (
        <button
          className="scroll-top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Înapoi sus"
        >
          ↑
        </button>
      )}

      {/* ── POPUP ─────────────────────────────────────────────── */}
      <PopupCTA />
    </div>
  );
}
